import { Button } from '@mui/material';
import { isEmpty, Modal } from '@vxui/components';
import { FormikErrors, FormikProvider, useFormik } from 'formik';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChangePassword, User } from '../../models';
import { useChangePasswordMutation, useLogout } from '../../services';
import { PasswordForm } from './PasswordForm';
import { maxPasswordLength, minPasswordLength } from './PasswordForm.helpers';

type ChangePasswordModalProps = {
  user: User;
  onClose: () => void;
};

export const changePasswordModalId = 'changePasswordModal';

export const ChangePasswordModal: FC<ChangePasswordModalProps> = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [changePassword] = useChangePasswordMutation();
  const logout = useLogout();

  const formik = useFormik<ChangePassword>({
    initialValues: {
      userID: user.userID,
      login: user.login,
      oldPassword: '',
      newPassword: '',
      confirmedNewPassword: '',
    },
    onSubmit: async ({ confirmedNewPassword: _, login: __, ...newPass }) => {
      try {
        await changePassword({ ...newPass }).unwrap();
        logout();
        onClose();
      } catch (e) {
        console.error("Unable to change user's password", e);
      }
    },
    validateOnBlur: true,
    validate: values => {
      const error = (fieldName: string, errorType: string, values = {}) =>
        t(`auth.forms.password.fields.${fieldName}.errors.${errorType}`, values);

      const errors: FormikErrors<ChangePassword> = {};

      // old
      if (isEmpty(values.oldPassword)) errors.oldPassword = error('oldPassword', 'empty');

      // new
      if (isEmpty(values.newPassword)) errors.newPassword = error('newPassword', 'empty');
      else if (values.newPassword.trim().length < minPasswordLength)
        errors.newPassword = error('newPassword', 'tooShort', { min: minPasswordLength });
      else if (values.newPassword.trim().length > maxPasswordLength)
        errors.newPassword = error('newPassword', 'tooLong', { max: maxPasswordLength });

      // confirmed
      if (values.newPassword !== values.confirmedNewPassword)
        errors.confirmedNewPassword = error('confirmedNewPassword', 'dontMatch');

      return errors;
    },
  });

  const submit = useCallback(() => formik.submitForm(), [formik]);

  return (
    <Modal
      open
      title={t('auth.modal.changePassword.title')}
      actions={
        <>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {t('button.close')}
          </Button>
          <Button onClick={submit} color="secondary" variant="contained">
            {t('button.save')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <PasswordForm />
      </FormikProvider>
    </Modal>
  );
};
